import { ASSETS } from "assets";
import FlexBox from "lib/components/common/flex-box";
import LoadingBox from "lib/components/common/loading-box";
import SimpleCard from "lib/components/common/simple-card";
import SimpleLogBook from "lib/components/common/simple-logbook";
import SimpleModal from "lib/components/common/simple-modal";
import DashboardLayout from "lib/components/layouts/dashboard";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { AppContext } from "lib/contexts/app-context";
import useClinics from "lib/hooks/useClinics";
import useLicense from "lib/hooks/useLicense";
import useLogging from "lib/hooks/useLogging";
import usePractitioner from "lib/hooks/usePractitioner";
import BookingService from "lib/services/appointment-booking-service";
import {
  formatDate,
  formatDateWithTime,
  getLocalISO,
} from "lib/utils/date-utils";
import { openUrlInNewTab } from "lib/utils/util";
import { DateTime } from "luxon";
import { FC, useContext, useEffect } from "react";
import { Col, Image, Row } from "react-bootstrap";
import { PiCheckCircleBold } from "react-icons/pi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const breadcrumbItems: BreadcrumbItem[] = [
  {
    label: "Subscriptions",
    path: PAGE_ROUTES.PRACTITIONER.Subscription,
  },
  {
    label: "Success",
    path: PAGE_ROUTES.PRACTITIONER.Subscription,
  },
];

interface PractitionerCheckoutSuccessPageProps {}

const PractitionerCheckoutSuccessPage: FC<
  PractitionerCheckoutSuccessPageProps
> = ({}) => {
  const navigateTo = useNavigate();

  const { appState } = useContext(AppContext);
  // const [loadingMessage, setLoadingMessage] =
  //   useState<string>("Please wait ...");
  const [searchParams] = useSearchParams();

  const { logItems, write2Log, lastLogItem } = useLogging({
    content: "Please wait ...",
    timestamp: formatDateWithTime(getLocalISO(new Date())),
  });

  const {
    getPractitionerSubscriptions,
    markSubscriptionAsPaid,
    uploadPractitionerLicenses,
    loading: practitionerLoading,
    error: practitionerError,
  } = usePractitioner();

  const {
    getClinicById,
    loading: clinicLoading,
    error: clinicError,
  } = useClinics();

  const {
    createLicenses,
    // loading: licenseLoading,
    // error: licenseError,
  } = useLicense();

  const handleLogItemClick = (item: LogItem) => {
    if (item.link) {
      openUrlInNewTab(item.link);
    }
  };

  const generateLicenses = async (
    subscription: PractitionerSubscriptionDetails,
    clinic: Clinic
  ) => {
    const designatedHours = BookingService.getClinicWorkingHours(
      clinic?.availableHours
    );

    write2Log("Preparing to create license agreements ...");
    const licenseDetails: LicenseDetails = {
      dates: subscription?.dates,
      licensePeriodStartDateObject: new Date(subscription?.fromDate),
      licensePeriodStart: subscription?.fromDate,
      licenseDateObject: new Date(),
      licenseDate: formatDate(getLocalISO(new Date())),
      licenseTerminationDateObject: new Date(subscription?.toDate),
      terminationDate: formatDate(subscription?.toDate),
      carParkingFee: true,
      consultingRoom: `Room ${subscription?.room.toFixed(0)}`,
      designatedHours,
      licenseeAddress: clinic?.address,
      licenseeName: appState?.name,
      licenseFee: subscription?.perDayPrice,
      licenseFeeUnit: "Per Day",
      months: subscription?.months,
    };

    write2Log("Generating license agreements ...");

    const detailsSegments: LicenseDetails[] = [];
    const length = (subscription?.months || 3) / 3;
    let fromDate = new Date(licenseDetails.licensePeriodStartDateObject);
    for (let i = 0; i < length; i++) {
      let toDate = DateTime.fromJSDate(fromDate)
        .plus({ months: length })
        .toJSDate();

      detailsSegments.push({
        ...licenseDetails,
        index: i,
        licensePeriodStart: formatDate(getLocalISO(fromDate)),
        terminationDate: formatDate(getLocalISO(toDate)),
      });
      fromDate = DateTime.fromJSDate(toDate).plus({ days: 1 }).toJSDate();
    }

    const licenses = await Promise.all(
      detailsSegments.map((details) => createLicenses(details))
    );

    write2Log("Uploading license agreements ...");

    await Promise.all(
      licenses.map((license) =>
        uploadPractitionerLicenses(
          subscription.practitionerId,
          subscription.id,
          license
        )
      )
    );

    return Promise.resolve(licenses);
  };

  useEffect(() => {
    write2Log("Fetching your transaction details ...");
    const transactionId = searchParams.get("transactionId");

    if (!transactionId) {
      toast.error("Transaction ID is missing. Please try again.");
      return;
    }

    write2Log("Fetching your subscription details ...");
    getPractitionerSubscriptions(appState?.user_id, (subscriptions) => {
      const filteredSubscriptions = subscriptions.filter(
        (subscription) => subscription.id === transactionId
      );

      if (filteredSubscriptions.length === 0) {
        toast.error("No related subscriptions found. Please contact support");
        return;
      }

      const filteredSubscription = filteredSubscriptions?.[0];

      getClinicById(filteredSubscription?.clinicId, (clinic) => {
        generateLicenses(filteredSubscription, clinic).then((licenses) => {
          licenses.map(
            (license) =>
              license &&
              write2Log(
                "Document: " + license?.name,
                URL.createObjectURL(license)
              )
          );

          write2Log("Marking subscription as paid ...");
          markSubscriptionAsPaid(appState?.user_id, transactionId, () => {
            //navigateTo(PAGE_ROUTES.PRACTITIONER.Dashboard);
            write2Log("You are all set ...");
          });
        });
      });
    });
  }, []);

  return (
    <DashboardLayout breadcrumbItems={breadcrumbItems}>
      <Row className="justify-content-center">
        <Col sm="6">
          <SimpleCard error={practitionerError || clinicError}>
            <FlexBox
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <PiCheckCircleBold size={64} color="green" />
              <h3 className="text-center">
                Great! Your subscription is confirmed
              </h3>
            </FlexBox>
            <br />
            <p className="text-center">
              We are working on preparing awesome stuff for you to get you
              started with.
            </p>
            <FlexBox justifyContent="center" alignItems="center">
              <Image
                src={ASSETS.IMAGES.CLOCK}
                width={100}
                className="remove-bg"
              />
            </FlexBox>
            <FlexBox
              justifyContent="center"
              alignItems="center"
              className="mt-3"
            >
              <hr className="w-50" />
            </FlexBox>
            <br />
            <SimpleLogBook
              logItems={logItems}
              title="Log book"
              subTitle="Click on the license documents to view or download them."
              onClick={handleLogItemClick}
            />
          </SimpleCard>
        </Col>
      </Row>
      <SimpleModal
        open={practitionerLoading || clinicLoading}
        close={() => {}}
        size="sm"
      >
        <LoadingBox message={lastLogItem.content} />
      </SimpleModal>
    </DashboardLayout>
  );
};

export default PractitionerCheckoutSuccessPage;
